import { ONLINE_APPLICATION_QUOTE_SERVICE_BASE_URL, ENVIRONMENT_CLASS } from "env";
import { createCommonHeaders } from "./commonHeaders";
import { z } from "zod";
import {
    post_GetDirectTermOnlineQuote,
    post_GetGIWL_OnlineQuote,
    post_GetMLTA_OnlineQuote,
} from "./online-application-quote-service.openapi.zod";

/**
 * Mock MLTA quote response for local development
 * Used when VPN prevents access to the actual quote service
 */
const MOCK_MLTA_QUOTE_RESPONSE = {
    benefits: [
        {
            tier: "Deluxe",
            level: 2,
            airline: 150000.0,
            common: 75000.0,
            loss_of_life: 18750.0,
            hospital: 450.0,
            yearly_er: 450.0,
            all_accident: 112.5,
            state: "CA",
            minAge: 18,
            maxAge: 69,
            individual: 11.25,
            family: 14.75,
            correctKeycode: "241L0472AS",
        },
        {
            tier: "Economy",
            level: 3,
            airline: 75000.0,
            common: 37500.0,
            loss_of_life: 9375.0,
            hospital: 225.0,
            yearly_er: 225.0,
            all_accident: 56.25,
            state: "CA",
            minAge: 18,
            maxAge: 69,
            individual: 6.0,
            family: 8.0,
            correctKeycode: "241L0472AS",
        },
    ],
};

/**
 * Enable mock MLTA quotes for local development when VPN is not available
 * Set to true to bypass the actual API call
 */
const USE_MOCK_QUOTES = ENVIRONMENT_CLASS === "loc" && !(window as any).Cypress;

/**
 * Mock DTO quote response for local development
 * Used when VPN prevents access to the actual quote service
 */
const MOCK_DTO_QUOTE_RESPONSE = {
    quotes: [
        { coverageAmount: 300000, totalPremiumAmount: 11.5 },
        { coverageAmount: 200000, totalPremiumAmount: 22.75 },
        { coverageAmount: 100000, totalPremiumAmount: 45.0 },
        { coverageAmount: 50000, totalPremiumAmount: 45.0 },
    ],
};

/**
 * Mock GIWL quote response for local development
 * Used when VPN prevents access to the actual quote service
 */
const MOCK_GIWL_QUOTE_RESPONSE = {
    errorResponseDataList: [{ errorNumber: "0", errorCode: "", errorMessageID: "", errorMessage: "" }],
    dataList: [
        { basePlan: "GW22", baseAmount: 3000, monthlyPremium: 21 },
        { basePlan: "GW22", baseAmount: 5000, monthlyPremium: 27 },
        { basePlan: "GW22", baseAmount: 7000, monthlyPremium: 35 },
        { basePlan: "GW22", baseAmount: 10000, monthlyPremium: 46 },
        { basePlan: "GW22", baseAmount: 15000, monthlyPremium: 66 },
        { basePlan: "GW22", baseAmount: 20000, monthlyPremium: 77 },
        { basePlan: "GW22", baseAmount: 25000, monthlyPremium: 93 },
    ],
};

export type DirectTermOnlineQuoteRequestBody = z.infer<typeof post_GetDirectTermOnlineQuote.parameters.shape.body>;
export type DirectTermOnlineQuoteResponseBody = z.infer<typeof post_GetDirectTermOnlineQuote.response>;

export async function getDirectTermOnlineQuote(
    body: DirectTermOnlineQuoteRequestBody
): Promise<DirectTermOnlineQuoteResponseBody> {
    if (USE_MOCK_QUOTES) {
        console.info("[DEV] Using mock DTO quote response");
        return post_GetDirectTermOnlineQuote.response.parse(MOCK_DTO_QUOTE_RESPONSE);
    }

    const response = await fetch(`${ONLINE_APPLICATION_QUOTE_SERVICE_BASE_URL}/direct-term-online-quote`, {
        method: "POST",
        body: JSON.stringify(body),
        headers: {
            ...createCommonHeaders(window),
            accept: "application/json",
            "Content-Type": "application/json",
        },
    });
    if (response.ok) {
        return post_GetDirectTermOnlineQuote.response.parse(await response.json());
    } else if (response.status === 404) {
        console.warn(response);
        throw new Error("Unable to find quotes.");
    } else {
        console.warn(response);
        throw new Error("Unexpected response to request to find quotes.");
    }
}

type LoyaltyOfferRequestBody = z.infer<typeof post_GetMLTA_OnlineQuote.parameters.shape.body>;
const LoyaltyOfferResponseBodySchema = post_GetMLTA_OnlineQuote.response;
type LoyaltyOfferResponseBody = z.infer<typeof LoyaltyOfferResponseBodySchema>;

export async function getMLTAOnlineQuote(body: LoyaltyOfferRequestBody): Promise<LoyaltyOfferResponseBody> {
    // Return mock response for local development when VPN is not available
    if (USE_MOCK_QUOTES) {
        console.info("[DEV] Using mock MLTA quote response");
        return LoyaltyOfferResponseBodySchema.parse(MOCK_MLTA_QUOTE_RESPONSE);
    }

    const response = await fetch(`${ONLINE_APPLICATION_QUOTE_SERVICE_BASE_URL}/mlta-online-quote`, {
        method: "POST",
        body: JSON.stringify(body),
        headers: {
            ...createCommonHeaders(window),
            accept: "application/json",
            "Content-Type": "application/json",
        },
    });
    if (response.ok) {
        const json = await response.json();
        return LoyaltyOfferResponseBodySchema.parse(json);
    } else if (response.status === 404) {
        console.warn(response);
        throw new Error("Unable to find quotes.");
    } else {
        console.warn(response);
        throw new Error("Unexpected response to request to find quotes.");
    }
}

export type GIWLOnlineQuoteRequestBody = z.infer<typeof post_GetGIWL_OnlineQuote.parameters.shape.body>;
export type GIWLOnlineQuoteResponseBody = z.infer<typeof post_GetGIWL_OnlineQuote.response>;

export async function getGIWLOnlineQuote(body: GIWLOnlineQuoteRequestBody): Promise<GIWLOnlineQuoteResponseBody> {
    if (USE_MOCK_QUOTES) {
        console.info("[DEV] Using mock GIWL quote response");
        return post_GetGIWL_OnlineQuote.response.parse(MOCK_GIWL_QUOTE_RESPONSE);
    }

    const response = await fetch(`${ONLINE_APPLICATION_QUOTE_SERVICE_BASE_URL}/giwl-online-quote`, {
        method: "POST",
        body: JSON.stringify(body),
        headers: {
            ...createCommonHeaders(window),
            accept: "application/json",
            "Content-Type": "application/json",
        },
    });
    if (response.ok) {
        return post_GetGIWL_OnlineQuote.response.parse(await response.json());
    } else if (response.status === 404) {
        console.warn(response);
        throw new Error("Unable to find quotes.");
    } else {
        console.warn(response);
        throw new Error("Unexpected response to request to find quotes.");
    }
}
